import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import client from "../../api/client.js";

export default function SlaTable({ monitorId, monitorName }) {
  const { t } = useTranslation("monitor_detail");
  const [slaRows, setSlaRows] = useState(null);

  useEffect(() => {
    if (!monitorId) return;
    async function load() {
      try {
        const res = await client.get(`/monitors/${monitorId}/sla-report`, {
          params: { months: 3 },
        });
        setSlaRows(res.data.data ?? []);
      } catch {
        setSlaRows([]);
      }
    }
    load();
  }, [monitorId]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{t("sla.title")}</h2>
        <button
          type="button"
          className="text-xs text-teal-600 hover:underline"
          onClick={async () => {
            try {
              const res = await client.get(`/monitors/${monitorId}/sla-report`, {
                params: { months: 12, format: "csv" },
                responseType: "blob",
              });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `sla-${monitorName}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            } catch {
              toast.error(t("sla.error_download"));
            }
          }}
        >
          {t("sla.download_csv")}
        </button>
      </div>
      {slaRows === null ? (
        <p className="px-5 py-6 text-center text-sm text-gray-400">{t("sla.loading")}</p>
      ) : slaRows.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-gray-400">
          {t("sla.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-100">
              <tr>
                {[
                  t("sla.col_month"),
                  t("sla.col_uptime"),
                  t("sla.col_checks"),
                  t("sla.col_up_checks"),
                  t("sla.col_downtime"),
                ].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-xs font-medium uppercase tracking-wide text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {slaRows.map((row) => (
                <tr key={row.month} className="hover:bg-gray-50">
                  <td className="px-5 py-2.5 text-sm font-medium text-gray-700">{row.month}</td>
                  <td className="px-5 py-2.5 text-sm">
                    <span
                      className={[
                        "font-semibold",
                        row.uptime_pct == null
                          ? "text-gray-400"
                          : row.uptime_pct >= 99.9
                          ? "text-green-600"
                          : row.uptime_pct >= 99
                          ? "text-yellow-600"
                          : "text-red-600",
                      ].join(" ")}
                    >
                      {row.uptime_pct != null ? `${row.uptime_pct}%` : "—"}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-sm text-gray-500">{row.total_checks.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-sm text-gray-500">{row.up_checks.toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-sm text-gray-500">
                    {row.downtime_minutes > 0
                      ? row.downtime_minutes >= 60
                        ? `${(row.downtime_minutes / 60).toFixed(1)}h`
                        : `${row.downtime_minutes}m`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
